type StoragePolicyState = {
  hardResetVersion?: string;
  lastSoftCleanupAt?: number;
};

const STORAGE_POLICY_KEY = '__hh_storage_policy__';
const CONFIG_STORAGE_KEY = 'hh-agent-ts-config';
const LEGACY_CONFIG_STORAGE_KEY = 'able-pro-material-react-ts-config';
const MINIMAL_CONFIG_KEYS = [CONFIG_STORAGE_KEY, '__usrlng__'];

// Bump this value when you want to force a new one-time full cleanup on deploy.
const HARD_RESET_VERSION = '2026-02-25-login-mobile-fix-1';

// Periodic cleanup cadence (30 days). Change to 14 days if needed.
const SOFT_CLEANUP_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

const SOFT_CLEANUP_LOCAL_KEYS = [
  'offer',
  'isOfferEnabled',
  'offerName',
  'customerEmail',
  'customerId',
  'dtDevelopment',
  'isDTDevelopmentEnabled',
  'dtDevelopmentName',
  'dtId',
  'dtCommercialId'
];

const SOFT_CLEANUP_SESSION_KEYS = ['drawerOpened'];

const parsePolicyState = (raw: string | null): StoragePolicyState => {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as StoragePolicyState;
  } catch {
    return {};
  }
};

const savePolicyState = (state: StoragePolicyState) => {
  localStorage.setItem(STORAGE_POLICY_KEY, JSON.stringify(state));
};

export const runStorageMaintenance = () => {
  try {
    const newConfig = localStorage.getItem(CONFIG_STORAGE_KEY);
    const legacyConfig = localStorage.getItem(LEGACY_CONFIG_STORAGE_KEY);

    // One-time migration and cleanup of the legacy config key.
    if (!newConfig && legacyConfig) {
      localStorage.setItem(CONFIG_STORAGE_KEY, legacyConfig);
    }
    localStorage.removeItem(LEGACY_CONFIG_STORAGE_KEY);

    const policyState = parsePolicyState(localStorage.getItem(STORAGE_POLICY_KEY));
    const now = Date.now();

    if (policyState.hardResetVersion !== HARD_RESET_VERSION) {
      const preservedEntries = MINIMAL_CONFIG_KEYS.map((key) => [key, localStorage.getItem(key)] as const).filter(
        ([, value]) => value !== null
      );

      localStorage.clear();
      sessionStorage.clear();

      preservedEntries.forEach(([key, value]) => {
        if (value !== null) localStorage.setItem(key, value);
      });

      savePolicyState({
        hardResetVersion: HARD_RESET_VERSION,
        lastSoftCleanupAt: now
      });

      return { reloadedNeeded: true };
    }

    const shouldSoftCleanup = !policyState.lastSoftCleanupAt || now - policyState.lastSoftCleanupAt >= SOFT_CLEANUP_INTERVAL_MS;

    if (shouldSoftCleanup) {
      SOFT_CLEANUP_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
      SOFT_CLEANUP_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));

      savePolicyState({
        ...policyState,
        lastSoftCleanupAt: now
      });
    }

    return { reloadedNeeded: false };
  } catch (error) {
    console.error('[storage-maintenance] failed', error);
    return { reloadedNeeded: false };
  }
};
